/**
 * /news + /news/topic/[t] + /news/source/[s] + /news/tag/[t]
 *
 * Threads-style social feed for legal news. SSR-friendly hydrate (Day-1
 * uses CSR fetch via lib/news-api). Sticky filter bar, vertical card stack,
 * cursor-paginated load-more.
 */

import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useLang } from '../use-lang';
import { resolveHotNews } from '../i18n/hotnews';
import { NewsFilterBar } from '../components/news/NewsFilterBar';
import { NewsFeedColumn } from '../components/news/NewsFeedColumn';
import { NewsDigestSignup } from '../components/news/NewsDigestSignup';

type Variant = 'all' | 'topic' | 'source' | 'tag';

export interface NewsFeedRouteProps {
  variant: Variant;
}

export function NewsFeedRoute({ variant }: NewsFeedRouteProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);
  const params = useParams();

  const filter = useMemo(() => {
    if (variant === 'topic') return { topic: params.topic };
    if (variant === 'source') return { source: params.source };
    if (variant === 'tag') return { tag: params.tag };
    return undefined;
  }, [variant, params.topic, params.source, params.tag]);

  useEffect(() => {
    document.title = buildTitle(variant, params, copy);
    setMeta('description', copy.feed_meta_description);
    setMeta('og:title', buildTitle(variant, params, copy), true);
    setMeta('og:description', copy.feed_meta_description, true);
    setMeta('og:type', 'website', true);
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large');
  }, [variant, params, copy]);

  return (
    <main className="news-feed-shell">
      <a className="skip-link" href="#news-feed-list">
        {lang === 'en' ? 'Skip to feed' : 'Đến danh sách tin'}
      </a>

      <header style={{ marginBottom: 16 }}>
        <h1 className="h-display" style={{ fontSize: 'clamp(28px, 4vw, 36px)', margin: 0 }}>
          {pageHeading(variant, params, copy)}
        </h1>
        <p style={{ marginTop: 6, color: 'var(--asklaw-muted)', fontSize: 14 }}>
          {copy.feed_header_subtitle}
        </p>
      </header>

      {variant === 'all' && copy.feed_intro_card ? (
        <div className="banner" style={{ marginBottom: 16 }}>
          {copy.feed_intro_card}
        </div>
      ) : null}

      <NewsFilterBar />

      <div className="news-feed-grid">
        <div id="news-feed-list" className="news-feed-main">
          <NewsFeedColumn filter={filter} />
        </div>
        <aside className="news-feed-sidebar" aria-label={lang === 'en' ? 'Newsletter' : 'Bản tin'}>
          <NewsDigestSignup />
        </aside>
      </div>
    </main>
  );
}

function buildTitle(
  variant: Variant,
  params: { topic?: string; source?: string; tag?: string },
  copy: ReturnType<typeof resolveHotNews>,
): string {
  if (variant === 'topic' && params.topic) {
    const labelKey = `digest_topic_${params.topic}` as keyof typeof copy;
    const label = (copy as Record<string, string>)[labelKey] ?? params.topic;
    return `${label} — ${copy.feed_meta_title}`;
  }
  if (variant === 'tag' && params.tag) {
    const labelKey = `tag_${params.tag}` as keyof typeof copy;
    const label = (copy as Record<string, string>)[labelKey] ?? params.tag;
    return `${label} — ${copy.feed_meta_title}`;
  }
  if (variant === 'source' && params.source) {
    return `${params.source} — ${copy.feed_meta_title}`;
  }
  return copy.feed_meta_title;
}

function pageHeading(
  variant: Variant,
  params: { topic?: string; source?: string; tag?: string },
  copy: ReturnType<typeof resolveHotNews>,
): string {
  if (variant === 'topic' && params.topic) {
    const labelKey = `digest_topic_${params.topic}` as keyof typeof copy;
    return (copy as Record<string, string>)[labelKey] ?? params.topic;
  }
  if (variant === 'tag' && params.tag) {
    const labelKey = `tag_${params.tag}` as keyof typeof copy;
    return (copy as Record<string, string>)[labelKey] ?? params.tag;
  }
  if (variant === 'source' && params.source) {
    return params.source;
  }
  return copy.feed_header_title;
}

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}
